<script setup>
import { ref, inject, computed } from "vue";
import { useRoute } from "vue-router";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/v4/popover";
import { Button } from "@/components/v4/button";
import { Input } from "@/components/v4/form";
import { IconPhone, IconX, IconBackspace } from "@tabler/icons-vue";
import Toast from "@/helpers/toast.helper";
import { getSipLineExtension } from "@/helpers/sip-line-extension";

const route = useRoute();
const phoneNumber = ref("");
const isOpen = ref(false);

const jssip = inject("jssip", null);

// Must match SIP registration (agent_id or decoded user id) — not agent_id alone
const hasCallExtension = computed(() => {
  const ext = getSipLineExtension();
  return ext != null && String(ext).trim() !== "";
});

const isOnLeadDetailsPage = computed(
  () => route.name === "crm.crm.leads.detail"
);

const isCallInProgress = computed(() => {
  const state = jssip?.callState?.value ?? "idle";
  return ["ringing", "connecting", "connected"].includes(state);
});

const isPhoneDisabled = computed(
  () =>
    !hasCallExtension.value ||
    isOnLeadDetailsPage.value ||
    isCallInProgress.value
);

const disabledTooltip = computed(() => {
  if (isCallInProgress.value) return "Call in progress";
  if (!hasCallExtension.value)
    return "No SIP line for this user (needs agent_id or valid user id)";
  if (isOnLeadDetailsPage.value)
    return "Use the call button in the lead sidebar";
  return "";
});
const makeCall = jssip?.makeCall;
const isRegistered = jssip?.isRegistered;
const registrationError = computed(() => jssip?.registrationError?.value ?? null);
const registeredSipUser = computed(() => jssip?.registeredSipUser?.value ?? null);
const registeredSipAor = computed(() => jssip?.registeredSipAor?.value ?? null);

const dialpadKeys = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "*",
  "0",
  "#",
];

const appendDigit = (digit) => {
  phoneNumber.value += digit;
};

const deleteLastDigit = () => {
  phoneNumber.value = phoneNumber.value.slice(0, -1);
};

const handleCall = () => {
  const number = phoneNumber.value.trim();
  if (!number) {
    Toast.error("Please enter a phone number");
    return;
  }
  if (!jssip || !makeCall) {
    Toast.error("Call service is not available");
    return;
  }
  if (registrationError.value) {
    Toast.error(registrationError.value);
    return;
  }
  if (isRegistered && !isRegistered.value) {
    Toast.error("SIP phone is connecting. Please wait...");
    return;
  }
  const formatted = number.startsWith("0") ? "62" + number.slice(1) : number;
  makeCall(formatted);
  Toast.success(`Calling ${formatted}...`);
  phoneNumber.value = "";
  isOpen.value = false;
};

const close = () => {
  isOpen.value = false;
};

// Valid phone: has digits, minimum length for a real number (e.g. 0812 = 4 digits)
const hasValidPhoneNumber = computed(() => {
  const trimmed = phoneNumber.value.trim();
  const digitsOnly = trimmed.replace(/\D/g, "");
  return digitsOnly.length >= 4;
});

const isCallButtonDisabled = computed(() => !hasValidPhoneNumber.value);
</script>

<template>
  <template v-if="isPhoneDisabled">
    <span
      class="vf-flex vf-items-center vf-justify-center vf-cursor-not-allowed vf-rounded-md vf-p-1.5 vf-text-neutral-300 vf-opacity-60"
      :title="disabledTooltip"
    >
      <IconPhone size="20" class="vf-shrink-0" />
    </span>
  </template>
  <Popover v-else v-model:open="isOpen">
    <PopoverTrigger>
      <span
        class="vf-flex vf-items-center vf-justify-center vf-cursor-pointer vf-rounded-md vf-p-1.5 vf-transition-colors vf-text-neutral-400 hover:vf-bg-neutral-100 hover:vf-text-neutral-600"
      >
        <IconPhone size="20" class="vf-shrink-0" />
      </span>
    </PopoverTrigger>
    <PopoverContent
      class="vf-w-[240px] !vf-rounded-lg !vf-p-0 vf-font-inter vf-overflow-hidden"
      :side-offset="8"
    >
      <div class="vf-flex vf-flex-col">
          <div
            class="vf-flex vf-flex-col vf-gap-1 vf-border-b vf-border-neutral-200 vf-px-3 vf-py-3"
          >
          <div class="vf-flex vf-items-center vf-justify-between">
          <div class="vf-flex vf-items-center vf-gap-1.5">
            <IconPhone size="18" class="vf-text-neutral-500" />
            <h6 class="vf-text-sm vf-font-medium vf-text-neutral-900">
              Dialpad
            </h6>
          </div>
          <button
            type="button"
            class="vf-rounded vf-p-0.5 vf-text-neutral-400 hover:vf-bg-neutral-100 hover:vf-text-neutral-700 vf-transition-colors"
            @click="close"
          >
            <IconX size="16" />
          </button>
        </div>
          <p
            v-if="registrationError"
            class="vf-m-0 vf-text-xs vf-leading-snug vf-text-danger-600"
          >
            {{ registrationError }}
          </p>
          <div
            v-else-if="isRegistered && registeredSipUser"
            class="vf-flex vf-flex-col vf-gap-1"
          >
            <p class="vf-m-0 vf-text-xs vf-text-neutral-500">
              Line {{ registeredSipUser }} · registered
            </p>
            <p
              v-if="registeredSipAor"
              class="vf-m-0 vf-text-[11px] vf-leading-snug vf-text-neutral-400"
            >
              Inbound: PBX must send INVITE to
              <span class="vf-font-mono vf-text-neutral-600">{{
                registeredSipAor
              }}</span>
              (same user part). Otherwise this app never rings.
            </p>
          </div>
          <p
            v-else-if="hasCallExtension"
            class="vf-m-0 vf-text-xs vf-text-amber-700"
          >
            Connecting to phone service…
          </p>
        </div>

        <div class="vf-flex vf-flex-col vf-gap-3 vf-p-3">
          <div class="vf-relative vf-flex vf-items-center">
            <Input
              v-model="phoneNumber"
              placeholder="Enter Phone Number"
              class="vf-text-center vf-text-sm !vf-h-9"
              :class="{ '!vf-pr-9': phoneNumber }"
              readonly
            />
            <button
              v-if="phoneNumber"
              type="button"
              class="vf-absolute vf-right-2 vf-top-1/2 -vf-translate-y-1/2 vf-flex vf-cursor-pointer vf-items-center vf-justify-center vf-rounded vf-p-1 vf-text-neutral-500 hover:vf-bg-neutral-100 hover:vf-text-neutral-700 vf-transition-colors"
              @click="deleteLastDigit"
            >
              <IconBackspace size="18" />
            </button>
          </div>

          <div class="vf-grid vf-grid-cols-3 vf-gap-1.5">
            <Button
              v-for="key in dialpadKeys"
              :key="key"
              variant="outline"
              class="vf-h-9 vf-w-full vf-rounded-md vf-border-neutral-200 vf-text-sm vf-text-neutral-700 hover:vf-bg-neutral-100 vf-transition-colors"
              @click="appendDigit(key)"
            >
              {{ key }}
            </Button>
          </div>

          <div
            :class="[
              'vf-mx-auto',
              isCallButtonDisabled &&
                'vf-pointer-events-none vf-opacity-50 vf-cursor-not-allowed',
            ]"
          >
            <Button
              class="vf-h-11 vf-w-11 vf-rounded-full !vf-bg-success-500 hover:!vf-bg-success-600 vf-border-0"
              @click="handleCall"
            >
              <IconPhone size="20" class="vf-text-white" stroke-width="2" />
            </Button>
          </div>
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>
